import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ElementDefinition, OperatorDefinition } from '../../types';
import { configurationService } from '../../services/configurationService';

// Main query hook for all configuration data with ReactQuery caching
export const useConditionElementsQuery = () => {
  return useQuery({
    queryKey: ['conditionElements'],
    queryFn: () => configurationService.getConfiguration(),
    staleTime: 30 * 60 * 1000, // 30 minutes - config data is very stable and cacheable
  });
};

// Derived hook for single element (uses client-side filtering)
export const useElementDefinitionQuery = (elementName: string, enabled = true) => {
  const { data: config, ...rest } = useConditionElementsQuery();

  return {
    ...rest,
    data: React.useMemo(() => {
      if (!config || !elementName) return null;

      // Check regular elements first
      if (config.elements[elementName]) {
        return config.elements[elementName];
      }

      // Check custom signals
      if (config.custom_signals[elementName]) {
        return config.custom_signals[elementName];
      }

      return null;
    }, [config, elementName]),
    enabled: enabled && !!elementName,
  };
};

// Derived hook for elements by category (uses client-side filtering)
export const useElementValuesByCategoryQuery = (category: string, enabled = true) => {
  const { data: config, ...rest } = useConditionElementsQuery();

  return {
    ...rest,
    data: React.useMemo(() => {
      if (!config || !category) return [];

      const elements: ElementDefinition[] = [];

      // Filter regular elements by category
      Object.values(config.elements).forEach(element => {
        if (element.category === category) {
          elements.push(element);
        }
      });

      // Filter custom signals by category
      Object.values(config.custom_signals).forEach(signal => {
        if (signal.category === category) {
          elements.push(signal);
        }
      });

      return elements;
    }, [config, category]),
    enabled: enabled && !!category,
  };
};

export const useElementOperatorsQuery = (elementName: string, enabled = true) => {
  const { data: config, isLoading: isConfigLoading } = useConditionElementsQuery();
  const { data: element, isLoading: isElementLoading } = useElementDefinitionQuery(
    elementName,
    enabled
  );

  const operatorDefinitions = React.useMemo(() => {
    if (!element || !config) return [];

    return (element.operators ?? [])
      .map(op => config.operators[op])
      .filter((op): op is OperatorDefinition => !!op);
  }, [element, config]);

  return {
    operatorDefinitions,
    isLoading: isConfigLoading || isElementLoading,
  };
};
